import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Button, Pagination, Tag, Tile } from '@dsi/react-eds';
import { useTheme } from '../../hooks/useTheme';
import { niiService } from '../../services/niiService';
import { NiiRunStatus, ReportSummary } from './types/nii.types';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  [NiiRunStatus.QUEUED]: { label: 'Queued', color: 'grey' },
  [NiiRunStatus.RUNNING]: { label: 'Running', color: 'blue' },
  [NiiRunStatus.COMPLETE]: { label: 'Complete', color: 'green' },
  [NiiRunStatus.ERROR]: { label: 'Failed', color: 'red' },
  [NiiRunStatus.CANCELLED]: { label: 'Cancelled', color: 'grey' }
};

const AuditRunDetail: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 6 });

  const { data: run } = useQuery({
    queryKey: ['gpl-audit-run', runId],
    queryFn: () => niiService.getRun(runId!),
    enabled: !!runId,
    refetchOnWindowFocus: false
  });

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ['gpl-audit-run-reports', runId],
    queryFn: () => niiService.getRunReports(runId!),
    enabled: !!runId,
    refetchOnWindowFocus: false,
    refetchInterval: run?.status === NiiRunStatus.RUNNING || run?.status === NiiRunStatus.QUEUED ? 10_000 : false
  });

  const totalPages = Math.ceil(reports.length / pagination.pageSize);
  const paginatedReports = reports.slice(pagination.pageIndex * pagination.pageSize, (pagination.pageIndex + 1) * pagination.pageSize);

  const handleViewNode = (report: ReportSummary) => {
    navigate(`/nii/audit-reports/run/${runId}/${report.reportId}`, {
      state: { auditName: run?.name, nodeName: report.nodeName, auditResult: report.auditResult, gplVersion: run?.gplVersion }
    });
  };

  const renderAuditResult = (report: ReportSummary) => {
    if (report.status === NiiRunStatus.RUNNING) {
      return (
        <span>
          <i className="icon icon-loading" style={{ marginRight: '4px' }} />
          {report.complianceRate != null ? `${Math.round(report.complianceRate)}%` : '...'}
        </span>
      );
    }
    if (report.status === NiiRunStatus.QUEUED) {
      return (
        <ReactEDSWrapper component={Tag} color="grey">
          Not Started
        </ReactEDSWrapper>
      );
    }
    if (report.status === NiiRunStatus.COMPLETE) {
      const isCompliant = report.auditResult === 'compliant';
      return (
        <ReactEDSWrapper component={Tag} color={isCompliant ? 'green' : 'red'}>
          {isCompliant ? 'Compliant' : 'Non Compliant'}
        </ReactEDSWrapper>
      );
    }
    if (report.status === NiiRunStatus.ERROR) {
      return (
        <ReactEDSWrapper component={Tag} color="red">
          Error
        </ReactEDSWrapper>
      );
    }
    return '—';
  };

  const runStatus = run ? STATUS_CONFIG[run.status] || STATUS_CONFIG[NiiRunStatus.QUEUED] : null;
  const auditTitle = run?.name || 'GPL Audit';

  return (
    <div className={theme ? 'light-theme' : 'dark-theme'}>
      <div className="row">
        <div className="column sm-12">
          <ReactEDSWrapper component={Tile} title={auditTitle} id="audit-metadata-tile">
            <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
              <div>
                <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>Audit Type</span>
                <span>GPL Audit</span>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>Version</span>
                <span>{run?.gplVersion || '—'}</span>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>Status</span>
                {runStatus && (
                  <ReactEDSWrapper component={Tag} color={runStatus.color}>
                    {runStatus.label}
                  </ReactEDSWrapper>
                )}
              </div>
            </div>
          </ReactEDSWrapper>
        </div>
      </div>

      <div className="row">
        <div className="column sm-12">
          <ReactEDSWrapper
            component={Tile}
            title={`Nodes`}
            subtitle={
              reports.length > 0
                ? `${pagination.pageIndex * pagination.pageSize + 1} - ${Math.min((pagination.pageIndex + 1) * pagination.pageSize, reports.length)} of ${reports.length} items`
                : ''
            }
            id="audit-nodes-tile"
          >
            {isLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '24px 0' }}>
                <div className="loader small" />
                <p>Loading nodes…</p>
              </div>
            )}

            {!isLoading && reports.length === 0 && (
              <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                <i className="icon icon-info" style={{ fontSize: '24px', display: 'block', marginBottom: '8px' }} />
                <div style={{ fontWeight: 500 }}>No node reports found</div>
              </div>
            )}

            {!isLoading && reports.length > 0 && (
              <>
                <div className="table">
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '12px 16px', width: '200px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            Node Name <i className="icon icon-sorting" />
                          </div>
                        </th>
                        <th style={{ textAlign: 'left', padding: '12px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            Audit Result <i className="icon icon-sorting" />
                          </div>
                        </th>
                        <th style={{ textAlign: 'right', padding: '12px 16px', width: '200px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedReports.map((report) => (
                        <tr
                          key={report.reportId}
                          style={{ transition: 'background-color 0.2s ease' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = !theme ? '#404040' : '#DCDCDC';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '';
                          }}
                        >
                          <td style={{ padding: '12px 16px', borderTop: `1px solid ${!theme ? '#404040' : '#e0e0e0'}` }}>
                            {report.nodeName || 'Unknown'}
                          </td>
                          <td style={{ padding: '12px 16px', borderTop: `1px solid ${!theme ? '#404040' : '#e0e0e0'}` }}>
                            {renderAuditResult(report)}
                          </td>
                          <td
                            style={{ padding: '12px 16px', borderTop: `1px solid ${!theme ? '#404040' : '#e0e0e0'}`, textAlign: 'right' }}
                          >
                            {report.status === NiiRunStatus.COMPLETE && (
                              <ReactEDSWrapper component={Button} onClick={() => handleViewNode(report)}>
                                View Details
                              </ReactEDSWrapper>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 0 && (
                  <ReactEDSWrapper
                    component={Pagination}
                    numPages={totalPages}
                    selectedPage={pagination.pageIndex + 1}
                    onPaginationChangePage={(page: number) => {
                      setPagination((prev) => ({ ...prev, pageIndex: page - 1 }));
                    }}
                    optionsList={[
                      { value: '6', label: '6', active: pagination.pageSize === 6 },
                      { value: '10', label: '10', active: pagination.pageSize === 10 },
                      { value: '20', label: '20', active: pagination.pageSize === 20 }
                    ]}
                    onPaginationChangeSelect={(size: string) => {
                      setPagination({ pageSize: Number(size), pageIndex: 0 });
                    }}
                    withActions
                  />
                )}
              </>
            )}
          </ReactEDSWrapper>
        </div>
      </div>
    </div>
  );
};

export default AuditRunDetail;
